<!-- 被検査者の情報画面 -->

<template>
  <div class="whole-wrap">
    <BurgerMenuComponent :isGradation="false" />

    <!-- 被検査者の情報 -->
    <div class="setting setting--person-inspected">
      <div class="setting-title">{{ $t('subject.title') }}</div>
      <div class="setting-explanation">{{ $t('subject.required_message') }}</div>
      <!-- 被検査者ID入力フォーム -->
      <div class="setting-form setting-form--config mt-32">
        <div class="form-label">{{ $t('subject.subject_id') }}<span class="form-required ml-8">{{ $t('subject.required_label') }}</span></div>
        <input type="text" class="input-text mt-8" id="subject_id" name="subject_id" v-model="subject_id" />
        <!-- 比検査者氏名入力フォーム -->
        <div class="form-label mt-24">{{ $t('subject.name') }}</div>
        <input type="text" class="input-text mt-8" id="subject_name" name="subject_name" v-model="subject_name" />
        <!-- 性別ラジオボタン -->
        <div class="form-label mt-24">{{ $t('subject.sex') }}</div>
        <div class="setting-form-age">
          <div class="radio mt-10 mr-24">
            <input type="radio" id="man" name="file-kinds" v-model="gender" :value="1" />
            <label class="u-fz14 u-dark-gray radio-label" for="man">{{ $t('subject.sex_male') }}</label>
          </div>
          <div class="radio mt-10 mr-24">
            <input type="radio" id="woman" name="file-kinds" v-model="gender" :value="2" />
            <label class="u-fz14 u-dark-gray radio-label" for="woman">{{ $t('subject.sex_female') }}</label>
          </div>
          <div class="radio mt-10">
            <input type="radio" id="others" name="file-kinds" v-model="gender" :value="3" />
            <label class="u-fz14 u-dark-gray radio-label" for="others">{{ $t('subject.sex_other') }}</label>
          </div>
        </div>
        <div class="radio mt-13">
          <input type="radio" id="none" name="file-kinds" v-model="gender" :value="0" />
          <label class="u-fz14 u-dark-gray radio-label" for="none">{{ $t('subject.sex_prefer_not_to_say') }}</label>
        </div>
        <!-- 年齢入力フォーム -->
        <div class="form-label mt-24">{{ $t('subject.age') }}</div>
        <div class="flex align-items-end">
          <input type="text" class="input-text form-mini mt-8" id="age" name="age" v-model="age" />
          <span class="form-label-age ml-8">{{ $t('subject.years_old_suffix') }}</span>
        </div>
      </div>
      <!-- 被検査者登録ボタン -->
      <div class="btn btn-base-next mt-40" @click="handleNextClick">{{ $t('subject.start') }}</div>
      <!-- エラーメッセージ表示 -->
      <div class="error-message mt-20" v-if="subjectErrorMessage" v-html="subjectErrorMessage"></div>
      <!-- コピーライト -->
      <div class="text-copyright">© 2025 Lion Corporation. All rights reserved.</div>
    </div>

    <!-- 同意画面 -->
    <ConsentDialog 
      :isVisible="showConsentDialog"
      @consent-response="handleConsentResponse"
    />
  </div>
</template>

<script>
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import BurgerMenuComponent from './BurgerMenu.vue';
import ConsentDialog from './ConsentDialog.vue';
import { checkStringInput, checkNumberInput, checkNameInput } from '@/components/common/inputValidations';
import {axiosClient} from '../utils/axiosClient';
import { findSubjectName, saveSubjectName } from './common/persistenceUtils';

export default {
  components: {
    BurgerMenuComponent,
    ConsentDialog,
  },
  setup() {
    const router = useRouter();
    const store = useStore();
    const { t } = useI18n();
    const subject_id = ref('');
    const subject_name = ref('');
    const operatorId = ref(store.getters['dataManagement/getOperatorId']);
    const gender = ref('');
    const age = ref('');
    const subjectErrorMessage = ref('');
    const showConsentDialog = ref(false);

    // 入力値のバリデーション
    const validateInputs = () => {
      subjectErrorMessage.value = '';

      if (!subject_id.value) {
        subjectErrorMessage.value = t('subject.error_subject_id_required');
        return false;
      }

      const idError = checkStringInput(subject_id.value);
      const ageError = checkNumberInput(age.value);
      const nameError = checkNameInput(subject_name.value);

      // ID がエラーの場合
      if (idError) {
        subjectErrorMessage.value += (subjectErrorMessage.value ? '<br>' : '') + `${t('subject.subject_id')}:${idError}`;
      }
      // 氏名 がエラーの場合
      if (nameError) {
        subjectErrorMessage.value += (subjectErrorMessage.value ? '<br>' : '') + `${t('subject.name')}:${nameError}`;
      }
      // パスワードがエラーの場合
      if (ageError) {
        subjectErrorMessage.value += (subjectErrorMessage.value ? '<br>' : '') + `${t('subject.age')}:${ageError}`;
      }
      // エラーがあるかどうかを返す
      return !idError && !nameError && !ageError;
    };

    // 同意画面のレスポンスハンドラー
    const handleConsentResponse = async (hasConsented) => {
      showConsentDialog.value = false;
      if (hasConsented) {
        // 同意情報を保存
        try {
          const response = await axiosClient.post('/api/consent', {
            operator_id: operatorId.value,
            subject_id: subject_id.value,
          });
          
          if (response.data.success) {
            // 同意情報の保存が成功したら登録処理を実行
            await subjectsRegist();
          } else {
            subjectErrorMessage.value = response.data.error || t('subject.error_consent_failed');
          }
        } catch (error) {
          console.error('同意情報保存エラー:', error);
          subjectErrorMessage.value = t('subject.error_consent_failed');
        }
      }
    };

    // 被検査者情報の登録APIへリクエスト
    const subjectsRegist = async () => {
      // 登録に必要なデータを構築します
      const registData = {
        operator_id: operatorId.value,
        subject_id: subject_id.value,
        gender: gender.value || '0',  // デフォルト値0を設定
        age: age.value || '0',        // デフォルト値0を設定
      };
      // 登録アクションを実行し、結果を待ちます
      const registResult = await store.dispatch('subjectRegist/regist', registData);
      // 登録が成功した場合
      if (registResult.success) {
        if (subject_name.value) {
          store.dispatch('dataManagement/setResultName', subject_name.value);
          saveSubjectName(subject_id.value, subject_name.value);
        } else {
          store.dispatch('dataManagement/setResultName', findSubjectName(subject_id.value));
        }

        // メニュー選択画面にリダイレクトします
        router.push({
          name: 'selectMenu',
          params: { operator_id: operatorId.value },
        });
      } else {
        // 登録が失敗した場合、エラーメッセージを表示します
        subjectErrorMessage.value = registResult.error;
      }
    };

    // チェックに進むボタンのクリックハンドラー
    const handleNextClick = async () => {
      if (validateInputs()) {
        await subjectsRegist();
      }
    };

    return {
      subject_id,
      subject_name,
      gender,
      age,
      subjectErrorMessage,
      showConsentDialog,
      handleNextClick,
      handleConsentResponse,
    };
  },
};
</script>

<style lang="scss" scoped>
@import '@/scss/style.scss';

.error-message {
  color: red;
}
</style>